import type {
    AuthStrategyConfigs,
    AuthStrategyKeys,
    ConfigOverrides,
    ONOPattern,
    OptionalHandlers
} from '@usl/usl-base-web-shell';
import { AppConfigs, ConfigKey, configureAuthStrategy, WLOverridingConfig } from '@usl/usl-base-web-shell';

import { NABEDGE } from '../constants';

/**
 * White Label Shell Configuration Example
 *
 * This file demonstrates how to configure the usl-base-web-shell library for your White Label partner project.
 * For detailed documentation, see {@link ../../usl-base-web-shell/CONFIGURATION_GUIDE.md}
 *
 * ## Quick Start Guide:
 *
 * ### Step 1: Choose ONO Pattern
 * - Pattern 1: Standard DAF authentication
 * - Pattern 3: Fed SSO with Trigger
 * - Pattern 4: Fed SSO with Passthrough
 *
 * ### Step 2: Configure Authentication Strategies
 * - Use functional groups (enableDafServicing, enableFederatedOnboarding, etc.)
 * - Override individual strategies as needed
 *
 * ### Step 3: Add Custom Handlers (Optional)
 * - Extend default behavior with business-specific logic
 * - All handlers run AFTER defaults complete
 *
 * ### Step 4: Apply Configuration Overrides (Optional)
 * - Customize specific behaviors (identity miniapp, token refresh, etc.)
 *
 * @see {@link ../../usl-base-web-shell/CONFIGURATION_GUIDE.md} For complete documentation
 * @see {@link ../../usl-base-web-shell/src/authStrategies/README.md} For auth strategy details
 *
 * @param appConfigs - Application configuration instance
 * @param onoPattern - ONO pattern override (optional, defaults to appConfigs value)
 * @returns Configured WLOverridingConfig
 */
export function getWLOverridingConfig(appConfigs: AppConfigs, onoPattern?: ONOPattern): WLOverridingConfig {
    // ============================================================================
    // STEP 1: Select ONO Pattern
    // ============================================================================
    const selectedPattern: ONOPattern = onoPattern ?? (appConfigs.get(ConfigKey.ONO_PATTERN, 'pattern1') as ONOPattern);

    // ============================================================================
    // STEP 3: Define Configuration Overrides (Optional)
    // ============================================================================
    const configOverrides: ConfigOverrides = {
        identityMiniAppOverrides: {
            // TODO: This value breaks the daf miniapp, so for now we will leave it for them to set.
            // If passed null DAF will use the correct endpoint according to the environment
            dafApiUrl: null
        }
    };

    // ============================================================================
    // STEP 4: Add Custom Handlers (Optional)
    // ============================================================================
    // All handlers are optional and run AFTER default handlers
    // Only include handlers you need for business-specific logic
    const optionalHandlers: OptionalHandlers = {};

    // ============================================================================
    // STEP 5: Select Authentication Strategies
    // ============================================================================
    const authStrategyKeys: AuthStrategyKeys = {
        // Individual overrides (optional):
        enableDafServicing: true,
        enableDafOnboarding: true
    };

    // ============================================================================
    // STEP 6: Build Auth Strategy Configuration
    // ============================================================================
    const authStrategyConfigs: AuthStrategyConfigs = {
        configOverrides,
        optionalHandlers
    };

    // ============================================================================
    // STEP 7: Build Complete White Label Configuration
    // ============================================================================

    // Parse numeric configurations
    const events = appConfigs.get(ConfigKey.EVENTS);
    const configuredIdleTime = appConfigs.get(ConfigKey.IDLE_TIME);
    const configuredEarlyCheckTime = appConfigs.get(ConfigKey.EARLY_CHECK_FOR_HEARTBEAT);
    const idleTime: number =
        configuredIdleTime && !Number.isNaN(parseInt(configuredIdleTime)) ? parseInt(configuredIdleTime) : 300;
    const earlyCheckFactorForHeartbeat: number =
        configuredEarlyCheckTime && !Number.isNaN(parseFloat(configuredEarlyCheckTime))
            ? parseFloat(configuredEarlyCheckTime)
            : 0.75;

    const config: WLOverridingConfig = {
        // Shell UI configurations
        shellConfigs: {
            AKAMAI_IMAGE_URL: appConfigs.get(ConfigKey.AKAMAI_IMAGE_URL),
            FIND_OUT_MORE_URL: appConfigs.get(ConfigKey.FIND_OUT_MORE_URL, null),
            CHAT_WIDGET_ASSET_MANIFEST: appConfigs.get(ConfigKey.CHAT_WIDGET_ASSET_MANIFEST),
            CONVERSATIONAL_MESSAGING_WIDGET_ASSET_MANIFEST: appConfigs.get(
                ConfigKey.CONVERSATIONAL_MESSAGING_WIDGET_ASSET_MANIFEST
            ),
            BROWSER_VERSIONS: appConfigs.get(ConfigKey.BROWSER_VERSIONS),
            NAB_IDP_USER_IDENTIFIER_MANIFEST_URL: appConfigs.get(ConfigKey.NAB_IDP_USER_IDENTIFIER_MANIFEST_URL),
            NAB_IDP_USER_VIEW_OUR_HELP_PAGE: appConfigs.get(ConfigKey.NAB_IDP_USER_VIEW_OUR_HELP_PAGE),
            INAPP_NOTIFICATIONS_ASSET_MANIFEST_URL: appConfigs.get(ConfigKey.INAPP_NOTIFICATIONS_ASSET_MANIFEST_URL),
            HOMESCREEN_MANIFEST_URL: appConfigs.get(ConfigKey.HOMESCREEN_MANIFEST_URL),
            ONO_PATTERN: selectedPattern
        },

        // Service integrations
        wlServiceConfig: {
            gbd: { LibraryUrl: appConfigs.get(ConfigKey.GBD_URL) },
            interaction: {
                endpoint: appConfigs.get(ConfigKey.FRAUD_INTERACTION_API),
                sessionEndpoint: appConfigs.get(ConfigKey.FRAUD_SESSION_API)
            },
            appDRUM_app_environment: appConfigs.get(ConfigKey.APPDRUM_APP_ENVIRONMENT),
            flags: {
                clientID: appConfigs.get(ConfigKey.LAUNCH_DARKLY_CLIENT_ID),
                initialData: {}
            }
        },

        // Authentication configuration
        wlAuthConfig: {
            environment: appConfigs.get(ConfigKey.AUTH_ENVIRONMENT, 'prod'),
            clientId: appConfigs.get(ConfigKey.AUTH_CLIENT_ID),
            scopes: appConfigs.get(ConfigKey.AUTH_SCOPE),
            tenant: appConfigs.get(ConfigKey.AUTH_TENANCY, 'wlnab')
        },

        // AppDynamics configuration
        appDConfig: {
            scriptUrl: appConfigs.get(ConfigKey.APPDRUM_SCRIPT_URL),
            appKey: appConfigs.get(ConfigKey.APPDRUM_APP_KEY),
            adrumExtUrlHttp: appConfigs.get(ConfigKey.APPDRUM_EXT_URL_HTTP),
            adrumExtUrlHttps: appConfigs.get(ConfigKey.APPDRUM_EXT_URL_HTTPS),
            beaconUrlHttp: appConfigs.get(ConfigKey.APPDRUM_BEACON_URL_HTTP),
            beaconUrlHttps: appConfigs.get(ConfigKey.APPDRUM_BEACON_URL_HTTPS),
            xdEnable: appConfigs.get(ConfigKey.APPDRUM_XDENABLE) === 'true',
            spa: appConfigs.get(ConfigKey.APPDRUM_SPA) === 'true'
        },

        // Core configurations
        navBaseUrl: appConfigs.get(ConfigKey.NAV_MANAGER_BASE_URL),
        dafEndPoint: appConfigs.get(ConfigKey.DAF_ENDPOINT),
        dafAuthRevokeEndPoint: appConfigs.get(ConfigKey.DAF_REVOKE_ENDPOINT),
        appDRum_BSN: appConfigs.get(ConfigKey.APPDRUM_BSN, ''),
        channelName: appConfigs.get(ConfigKey.CHANNEL_NAME, NABEDGE),
        rootContextId: appConfigs.get(ConfigKey.NAV_MANAGER_CONTEXT_ID, 'root'),
        serverBasePath: appConfigs.get(ConfigKey.SHELL_BASE_PATH, '/'),
        baseUrl: appConfigs.get(ConfigKey.BASE_URL, '/'),
        ldClientId: appConfigs.get(ConfigKey.LAUNCH_DARKLY_CLIENT_ID),

        // Keep-alive configuration
        keepAliveConfig: {
            idleTime,
            events: events ? events.split(',') : ['mousemove', 'mousedown', 'keypress', 'touchmove'],
            heartBeatEndPoint: appConfigs.get(ConfigKey.HEARTBEAT_URL),
            earlyCheckFactorForHeartbeat
        },

        // Brand configuration
        brand: {
            themeName: appConfigs.get(ConfigKey.BRAND_THEME, 'nab'),
            id: appConfigs.get(ConfigKey.BRAND_ID, 'nab'),
            title: appConfigs.get('BRAND_TITLE', 'NAB Edge'),
            brandText: appConfigs.get('BRAND_TEXT', 'NAB Edge')
        },

        // NAB-X configuration
        nabXConfig: {
            contentEndpoint: appConfigs.get('REMOTE_CONTENT_ENDPOINT'),
            themeEndpoint: appConfigs.get('REMOTE_THEME_ENDPOINT')
        },

        // Tenancy configuration
        tenancies: {
            full: appConfigs.get(ConfigKey.BRAND_ID, 'wlnab'),
            prospect: appConfigs.get(ConfigKey.AUTH_PROSPECT_TENANCY, 'wlnabprospect')
        },

        // User configuration
        userConfig: {
            org: 'NAB',
            type: 'customer'
        },

        // Configure authentication strategies using the helper function
        authStrategies: configureAuthStrategy(appConfigs, authStrategyKeys, authStrategyConfigs)
    };

    return config;
}
